import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Collapse,
  Stack,
  TextField,
  InputAdornment,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import InfoIcon from '@mui/icons-material/Info';
import ReceiptIcon from '@mui/icons-material/Receipt';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { format } from 'date-fns';
import trLocale from 'date-fns/locale/tr';
import PrintIcon from '@mui/icons-material/Print';
import ReplayIcon from '@mui/icons-material/Replay';
import WarningIcon from '@mui/icons-material/Warning';
import { styled } from '@mui/material/styles';
import { accountingApi } from '../../../config/apiClient';
import { ACCOUNTING_API } from '../../../config/api';
import AccountingDataTable from '../shared/AccountingDataTable';
import type { DataTableColumn } from '../shared/AccountingDataTable';
import { useTranslation } from 'react-i18next';

// Yazdırma için özel stil tanımlaması
const PrintableContent = styled('div')(({ theme }) => ({
  // Normal görünümde içeriği gizle
  display: 'none',
  // Yazdırma modunda stil
  '@media print': {
    display: 'block',
    width: '100%',
    margin: 0,
    padding: theme.spacing(2),
    '& h1': {
      fontSize: '18pt',
      marginBottom: theme.spacing(2),
      textAlign: 'center',
      fontWeight: 'bold'
    },
    '& h2': {
      fontSize: '14pt',
      marginBottom: theme.spacing(1)
    },
    '& table': {
      width: '100%',
      borderCollapse: 'collapse',
      marginTop: theme.spacing(2)
    },
    '& th, & td': {
      border: '1px solid #ddd',
      padding: theme.spacing(0.5, 1),
      fontSize: '10pt'
    },
    '& th': {
      backgroundColor: '#f8f8f8',
      fontWeight: 'bold',
      textAlign: 'left'
    }
  }
}));

// Yazdırma sırasında gizlenecek içerik
const NoPrint = styled('div')(({ theme }) => ({
  '@media print': {
    display: 'none'
  }
}));

// Transfer Fiş Arayüzü
interface TransferTicket {
  id: string;
  sourceWarehouseId: string;
  sourceWarehouseName: string;
  targetWarehouseId: string;
  targetWarehouseName: string;
  date: string;
  status: string;
  items: TransferItem[];
  totalItems: number;
}

// Transfer edilen ürün arayüzü
interface TransferItem {
  itemId: string;
  itemName: string;
  stockCode: string;
  quantity: number;
  unit: string;
}

// Transfer fişleri ana bileşeni
const TransferTickets: React.FC<{ companyId: string | null }> = ({ companyId }) => {
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const localeCode = isTr ? 'tr-TR' : 'en-US';
  const pickerLocale = isTr ? trLocale : undefined;
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);
  const [transfers, setTransfers] = useState<TransferTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState<TransferTicket | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  
  // Geri alma işlemleri için state'ler
  const [revertDialogOpen, setRevertDialogOpen] = useState(false);
  const [isReverting, setIsReverting] = useState(false);
  const [revertSuccess, setRevertSuccess] = useState<string | null>(null);
  const [revertError, setRevertError] = useState<string | null>(null);
  
  // ✅ Server-side pagination/sorting state
  const [page, setPage] = useState(0); // 0-based
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [sortColumn, setSortColumn] = useState<string>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const fetchIdRef = useRef(0); // Race condition koruması
  
  // Tarih filtresi
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [debouncedStartDate, setDebouncedStartDate] = useState<string>('');
  const [debouncedEndDate, setDebouncedEndDate] = useState<string>('');

  // ✅ Arama debounce (400ms)
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(0);
    }, 400);
    return () => { if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current); };
  }, [searchTerm]);

  // ✅ Tarih filtresi değiştiğinde sayfa sıfırla
  useEffect(() => {
    setDebouncedStartDate(startDate);
    setDebouncedEndDate(endDate);
    setPage(0);
  }, [startDate, endDate]);

  // ✅ Server-side sayfalı transfer fişi yükleme
  const loadTransfersPaged = useCallback(async () => {
    if (!companyId) return;
    const fetchId = ++fetchIdRef.current;
    
    try {
      setLoading(true);
      console.log('📦 [TRANSFERS] Sayfalı API\'den transfer fişleri yükleniyor...');
      
      const response = await accountingApi.post(
        ACCOUNTING_API.INVENTORY_WAREHOUSE_TRANSFERS_PAGED,
        {
          pageNumber: page + 1,
          pageSize: rowsPerPage,
          searchTerm: debouncedSearchTerm || undefined,
          startDate: debouncedStartDate || undefined,
          endDate: debouncedEndDate || undefined,
          sortColumn: sortColumn,
          sortDirection: sortDirection,
        }
      );
      
      if (fetchId !== fetchIdRef.current) return; // Race condition
      
      const result = response.data;
      const items = result.items || [];
      
      const transfersList: TransferTicket[] = items.map((data: any) => ({
        id: data.id || '',
        sourceWarehouseId: data.sourceWarehouseId || '',
        sourceWarehouseName: data.sourceWarehouseName || '',
        targetWarehouseId: data.targetWarehouseId || '',
        targetWarehouseName: data.targetWarehouseName || '',
        date: data.date || '',
        status: data.status || 'completed',
        items: data.items || [],
        totalItems: data.totalItems || 0
      }));
      
      console.log(`✅ [TRANSFERS] ${transfersList.length} transfer fişi yüklendi (Toplam: ${result.totalCount || 0})`);
      setTransfers(transfersList);
      setTotalCount(result.totalCount || 0);
      setLoading(false);
    } catch (err: any) {
      if (fetchId !== fetchIdRef.current) return;
      console.error('❌ [TRANSFERS] Transfer fişleri yüklenirken hata:', err);
      setError(tt('Transfer fişleri yüklenirken bir hata oluştu.', 'An error occurred while loading transfer tickets.'));
      setLoading(false);
    }
  }, [companyId, page, rowsPerPage, debouncedSearchTerm, debouncedStartDate, debouncedEndDate, sortColumn, sortDirection]);

  // ✅ Herhangi bir filtre/sıralama/sayfa değiştiğinde refetch
  useEffect(() => {
    loadTransfersPaged();
  }, [loadTransfersPaged]);

  // Fiş detaylarını göster
  const handleViewDetails = (transfer: TransferTicket) => {
    setSelectedTransfer(transfer);
    setDetailDialogOpen(true);
  };

  // Fiş detay modalını kapat
  const handleCloseDetailDialog = () => {
    setDetailDialogOpen(false);
    setSelectedTransfer(null);
  };

  // Geri alma onay modalını aç
  const handleOpenRevertDialog = () => {
    setRevertDialogOpen(true);
    setRevertError(null);
    setRevertSuccess(null);
  };

  // Geri alma onay modalını kapat
  const handleCloseRevertDialog = () => {
    setRevertDialogOpen(false);
  };

  // Transfer işlemini geri al (SQL API)
  const handleRevertTransfer = async () => {
    if (!selectedTransfer || !companyId) {
      setRevertError(tt('Transfer seçili değil veya şirket bilgisi bulunamadı.', 'Transfer is not selected or company information is missing.'));
      return;
    }

    try {
      setIsReverting(true);
      setRevertError(null);
      
      console.log('🔄 [TRANSFERS] Transfer geri alınıyor:', selectedTransfer.id);
      
      // SQL API'ye geri alma isteği gönder
      await accountingApi.post(
        ACCOUNTING_API.INVENTORY_WAREHOUSE_TRANSFER_REVERSE(selectedTransfer.id),
        { reversedBy: 'Current User' } // Kullanıcı bilgisi eklenebilir
      );
      
      console.log('✅ [TRANSFERS] Transfer başarıyla geri alındı');
      setRevertSuccess(tt('Transfer işlemi başarıyla geri alındı.', 'Transfer operation has been reverted successfully.'));
      
      // Transfer listesini yenile
      setTimeout(async () => {
        try {
          await loadTransfersPaged();
        } catch (err) {
          console.error('Transfer listesi yenilenemedi:', err);
        }
        
        // Modalları kapat
        setRevertDialogOpen(false);
        setDetailDialogOpen(false);
        setSelectedTransfer(null);
        setRevertSuccess(null);
      }, 1500);
      
    } catch (err: any) {
      console.error('❌ [TRANSFERS] Transfer geri alma hatası:', err);
      const errorMessage = err.response?.data?.message || err.message || tt('Bilinmeyen bir hata oluştu', 'An unknown error occurred');
      setRevertError(`${tt('İşlem sırasında hata oluştu', 'An error occurred during operation')}: ${errorMessage}`);
    } finally {
      setIsReverting(false);
    }
  };
  
  // Yazdır fonksiyonu
  const handlePrint = () => {
    setTimeout(() => {
      window.print();
    }, 100);
  };

  // Filtreleri aç/kapat
  const toggleFilters = () => {
    setIsFilterOpen(!isFilterOpen);
  };

  // Duruma göre chip rengi
  const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  // AccountingDataTable kolon tanımları
  const transferColumns = useMemo<DataTableColumn<TransferTicket>[]>(() => [
    {
      id: 'id',
      label: tt('Fiş No', 'Ticket No'),
      sortable: true,
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <ReceiptIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
          {row.id}
        </Box>
      ),
    },
    {
      id: 'date',
      label: tt('Tarih', 'Date'),
      field: 'date',
      sortable: true,
      render: (row) => <>{formatDate(row.date)}</>,
    },
    {
      id: 'sourceWarehouseName',
      label: tt('Kaynak Depo', 'Source Warehouse'),
      field: 'sourceWarehouseName',
      sortable: true,
      render: (row) => (
        <Chip icon={<WarehouseIcon />} label={row.sourceWarehouseName} size="small" color="primary" variant="outlined" />
      ),
    },
    {
      id: 'targetWarehouseName',
      label: tt('Hedef Depo', 'Target Warehouse'),
      field: 'targetWarehouseName',
      sortable: true,
      render: (row) => (
        <Chip icon={<WarehouseIcon />} label={row.targetWarehouseName} size="small" color="secondary" variant="outlined" />
      ),
    },
    {
      id: 'totalItems',
      label: tt('Ürün Sayısı', 'Item Count'),
      field: 'totalItems',
      sortable: true,
    },
    {
      id: 'status',
      label: tt('Durum', 'Status'),
      sortable: true,
      render: (row) => (
        <Chip
          label={row.status === 'completed' ? tt('Tamamlandı', 'Completed') : row.status === 'pending' ? tt('Bekliyor', 'Pending') : tt('İptal Edildi', 'Cancelled')}
          size="small"
          color={getStatusColor(row.status)}
          variant="outlined"
        />
      ),
    },
    {
      id: 'actions',
      label: tt('İşlemler', 'Actions'),
      render: (row) => (
        <Tooltip title={tt('Detayları Görüntüle', 'View Details')}>
          <IconButton size="small" color="primary" onClick={() => handleViewDetails(row)}>
            <InfoIcon />
          </IconButton>
        </Tooltip>
      ),
    },
  ], [isTr]);

  // Tarih formatla
  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'dd MMM yyyy HH:mm', { locale: pickerLocale });
    } catch (error) {
      return dateString;
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Başlık ve Filtreler */}
      <NoPrint>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" component="div">
            {tt('Transfer Fişleri', 'Transfer Tickets')}
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              size="small"
              placeholder={tt('Fiş No, Depo veya Ürün Ara...', 'Search by Ticket No, Warehouse or Item...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 250 }}
            />
            
            <Button
              variant="outlined"
              size="small"
              endIcon={isFilterOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              onClick={toggleFilters}
            >
              {tt('Filtreler', 'Filters')}
            </Button>
          </Box>
        </Box>
      </NoPrint>
      
      {/* Filtre Alanı */}
      <NoPrint>
        <Collapse in={isFilterOpen}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  label={tt('Başlangıç Tarihi', 'Start Date')}
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  label={tt('Bitiş Tarihi', 'End Date')}
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Button 
                  fullWidth 
                  variant="contained" 
                  color="primary"
                  onClick={() => {
                    setStartDate('');
                    setEndDate('');
                    setPage(0);
                  }}
                >
                  {tt('Temizle', 'Clear')}
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Collapse>
      </NoPrint>

      {/* Ana İçerik */}
      <NoPrint>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : totalCount === 0 && transfers.length === 0 ? (
          <Box sx={{ 
            p: 4, 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            justifyContent: 'center',
            height: '400px',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #e8ecf2 100%)',
            borderRadius: 2
          }}>
            <ReceiptIcon sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
            <Typography variant="h5" component="h2" gutterBottom color="primary.main" fontWeight="bold">
              {tt('Transfer Fişleri', 'Transfer Tickets')}
            </Typography>
            <Typography variant="body1" color="text.secondary" align="center">
              {searchTerm || startDate || endDate 
                ? tt('Arama ve filtre kriterlerine uygun transfer fişi bulunamadı.', 'No transfer ticket found matching search/filter criteria.') 
                : tt('Henüz transfer fişi bulunmamaktadır.', 'No transfer ticket found yet.')}
            </Typography>
          </Box>
        ) : (
          <AccountingDataTable<TransferTicket>
            columns={transferColumns}
            rows={transfers}
            getRowId={(row) => row.id}
            loading={false}
            defaultRowsPerPage={25}
            defaultSortField="date"
            defaultSortDirection="desc"
            emptyMessage={tt('Transfer fişi bulunamadı.', 'No transfer ticket found.')}
            emptyIcon={<ReceiptIcon sx={{ fontSize: 48, color: 'text.disabled' }} />}
            ariaLabel={tt('transfer fişleri tablosu', 'transfer tickets table')}
            // ✅ Server-side pagination
            serverSidePagination={true}
            totalCount={totalCount}
            page={page}
            pageSize={rowsPerPage}
            onPageChange={(newPage, newPageSize) => {
              setPage(newPage);
              setRowsPerPage(newPageSize);
            }}
            // ✅ Server-side sorting
            sortField={sortColumn}
            sortDirection={sortDirection}
            onSort={(field, direction) => {
              setSortColumn(field);
              setSortDirection(direction);
              setPage(0);
            }}
            columnResizeStorageKey="transfer-tickets"
          />
        )}
      </NoPrint>

      {/* Transfer Fiş Detay Modalı */}
      <Dialog
        open={detailDialogOpen}
        onClose={handleCloseDetailDialog}
        maxWidth="md"
        fullWidth
      >
        <NoPrint>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6">
                {tt('Transfer Fiş Detayları', 'Transfer Ticket Details')}
              </Typography>
              <Chip 
                label={`${tt('Fiş No', 'Ticket No')}: ${selectedTransfer?.id || ''}`} 
                color="primary"
                variant="outlined"
              />
            </Box>
          </DialogTitle>
        </NoPrint>
        <DialogContent dividers>
          {selectedTransfer && (
            <>
              <NoPrint>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">{tt('Tarih', 'Date')}</Typography>
                    <Typography variant="body1">{formatDate(selectedTransfer.date)}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">{tt('Durum', 'Status')}</Typography>
                    <Chip 
                      label={selectedTransfer.status === 'completed' ? tt('Tamamlandı', 'Completed') : 
                            selectedTransfer.status === 'pending' ? tt('Bekliyor', 'Pending') : tt('İptal Edildi', 'Cancelled')} 
                      size="small" 
                      color={getStatusColor(selectedTransfer.status)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">{tt('Kaynak Depo', 'Source Warehouse')}</Typography>
                    <Typography variant="body1">
                      <WarehouseIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                      {selectedTransfer.sourceWarehouseName}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">{tt('Hedef Depo', 'Target Warehouse')}</Typography>
                    <Typography variant="body1">
                      <WarehouseIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                      {selectedTransfer.targetWarehouseName}
                    </Typography>
                  </Grid>
                </Grid>
                
                <Divider sx={{ my: 2 }} />
                
                <Typography variant="subtitle1" gutterBottom>
                  {tt('Transfer Edilen Ürünler', 'Transferred Items')} ({selectedTransfer.items.length})
                </Typography>
                
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>{tt('Ürün Adı', 'Item Name')}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>{tt('Stok Kodu', 'Stock Code')}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>{tt('Miktar', 'Quantity')}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>{tt('Birim', 'Unit')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedTransfer.items.map((item, index) => (
                        <TableRow key={index} hover>
                          <TableCell>{item.itemName}</TableCell>
                          <TableCell>{item.stockCode || '-'}</TableCell>
                          <TableCell>{item.quantity}</TableCell>
                          <TableCell>{item.unit}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </NoPrint>
              
              {/* Yazdırma için görünüm */}
              <PrintableContent>
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h1">
                    {tt('Depo Transfer Fişi', 'Warehouse Transfer Ticket')}
                  </Typography>
                  <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h2">
                      {tt('Fiş No', 'Ticket No')}: {selectedTransfer.id}
                    </Typography>
                    <Typography variant="h2">
                      {tt('Tarih', 'Date')}: {formatDate(selectedTransfer.date)}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {tt('Kaynak Depo', 'Source Warehouse')}: {selectedTransfer.sourceWarehouseName}
                    </Typography>
                    <ArrowRightAltIcon sx={{ mx: 2 }} />
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {tt('Hedef Depo', 'Target Warehouse')}: {selectedTransfer.targetWarehouseName}
                    </Typography>
                  </Box>
                  
                  <table>
                    <thead>
                      <tr>
                        <th style={{ width: '40%' }}>{tt('Ürün Adı', 'Item Name')}</th>
                        <th style={{ width: '20%' }}>{tt('Stok Kodu', 'Stock Code')}</th>
                        <th style={{ width: '20%' }}>{tt('Miktar', 'Quantity')}</th>
                        <th style={{ width: '20%' }}>{tt('Birim', 'Unit')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedTransfer.items.map((item, index) => (
                        <tr key={index}>
                          <td>{item.itemName}</td>
                          <td>{item.stockCode || '-'}</td>
                          <td>{item.quantity}</td>
                          <td>{item.unit}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  
                  <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 3 }}>
                        {tt('Teslim Eden:', 'Delivered By:')}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 10 }}>
                        {tt('İmza:', 'Signature:')}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 3 }}>
                        {tt('Teslim Alan:', 'Received By:')}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 10 }}>
                        {tt('İmza:', 'Signature:')}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </PrintableContent>
            </>
          )}
        </DialogContent>
        <NoPrint>
          <DialogActions>
            <Button 
              variant="outlined" 
              color="error" 
              startIcon={<ReplayIcon />} 
              onClick={handleOpenRevertDialog}
            >
              {tt('İşlemleri Geri Al', 'Revert Operations')}
            </Button>
            <Button 
              variant="outlined" 
              color="primary" 
              startIcon={<PrintIcon />} 
              onClick={handlePrint}
            >
              {tt('Yazdır', 'Print')}
            </Button>
            <Button onClick={handleCloseDetailDialog} color="primary">
              {tt('Kapat', 'Close')}
            </Button>
          </DialogActions>
        </NoPrint>
      </Dialog>

      {/* İşlemleri Geri Alma Onay Modalı */}
      <NoPrint>
        <Dialog
          open={revertDialogOpen}
          onClose={!isReverting ? handleCloseRevertDialog : undefined}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="warning" />
            {tt('Transfer İşlemini Geri Al', 'Revert Transfer Operation')}
          </DialogTitle>
          <DialogContent>
            {revertError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {revertError}
              </Alert>
            )}
            
            {revertSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {revertSuccess}
              </Alert>
            )}
            
            <Typography variant="body1" paragraph>
              <strong>{tt('DİKKAT:', 'WARNING:')}</strong> {tt('Bu işlem kesinlikle geri alınamaz. Transfer edilen tüm ürünler kaynak depoya geri aktarılacak ve transfer fişi silinecektir.', 'This action cannot be undone. All transferred products will be moved back to the source warehouse and the transfer ticket will be deleted.')}
            </Typography>
            
            {selectedTransfer && (
              <Box sx={{ backgroundColor: 'grey.100', p: 2, borderRadius: 1, mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {tt('Transfer Bilgileri:', 'Transfer Information:')}
                </Typography>
                <Typography variant="body2">
                  <strong>{tt('Fiş No:', 'Ticket No:')}</strong> {selectedTransfer.id}
                </Typography>
                <Typography variant="body2">
                  <strong>{tt('Tarih:', 'Date:')}</strong> {formatDate(selectedTransfer.date)}
                </Typography>
                <Typography variant="body2">
                  <strong>{tt('Kaynak Depo:', 'Source Warehouse:')}</strong> {selectedTransfer.sourceWarehouseName}
                </Typography>
                <Typography variant="body2">
                  <strong>{tt('Hedef Depo:', 'Target Warehouse:')}</strong> {selectedTransfer.targetWarehouseName}
                </Typography>
                <Typography variant="body2">
                  <strong>{tt('Toplam Ürün Sayısı:', 'Total Item Count:')}</strong> {selectedTransfer.totalItems}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={handleCloseRevertDialog} 
              color="primary" 
              disabled={isReverting}
            >
              {tt('İptal', 'Cancel')}
            </Button>
            <Button 
              onClick={handleRevertTransfer} 
              color="error" 
              variant="contained"
              disabled={isReverting}
              startIcon={isReverting ? <CircularProgress size={24} color="inherit" /> : <ReplayIcon />}
            >
              {isReverting ? tt('İşlem Gerçekleştiriliyor...', 'Processing...') : tt('İşlemleri Geri Al', 'Revert Operations')}
            </Button>
          </DialogActions>
        </Dialog>
      </NoPrint>
    </Box>
  );
};

export default TransferTickets; 